// NPM Imports
import React, { useContext, useEffect } from 'react'
// Quote Context
import { useQuoteContext } from '../../hooks/useQuoteContext'
// Component Imports
// JS Imports
import {
   // @ts-ignore
   formatFloatAsPercent,
   // @ts-ignore
   removeDiscountNameVariables,
} from '../../common/helpers.mjs'
import { shouldDisableDiscount } from '../../js/validate'
import DiscountDescriptionBox from './DiscountDescriptionBox'
// Discount Logic Imports
import {
   shouldDiscountBeChecked,
   shouldInitializeDefaultOn,
   initializeDefaultOnDiscount,
   toggleDefaultOnDiscount,
   shouldInitializeAlwaysApply,
   initializeAlwaysApplyDiscount,
   type DiscountState,
} from '../../utils/discountLogic'
// Asset Imports

interface BooleanDiscountProps {
   discount_key: string
   order?: number
}

export default function BooleanDiscount({
   discount_key,
   order = 99,
}: BooleanDiscountProps) {
   const state = useQuoteContext()

   // A new name for the discount that replaces $PRODUCTNAME with the actual product name if it applies to only one product
   let formattedDiscountName = ''
   if (
      state.discount_list?.[discount_key]?.['DiscountName']?.includes(
         '$PRODUCTNAME',
      )
   ) {
      if (
         state.discount_list?.[discount_key]?.['ApplyTo']?.split(',')?.length ==
         1
      )
         formattedDiscountName = state.discount_list?.[discount_key]?.[
            'DiscountName'
         ]?.replace(
            /\$PRODUCTNAME/g,
            state.product_list?.[
               state.discount_list?.[discount_key]?.['ApplyTo']
            ]?.['ProductName'],
         )
      else
         formattedDiscountName = removeDiscountNameVariables(
            state.discount_list?.[discount_key]?.['DiscountName'],
         )
   } else
      formattedDiscountName =
         state.discount_list?.[discount_key]?.['DiscountName']

   // Helpers to manage discount's checkbox states
   let shouldAlwaysApply =
      state.discount_list[discount_key]?.['AlwaysApply'] ?? false
   // Disable state excluding deal desk locks — drives the checked state and the auto
   // (de)selection effect, so a deal desk lock alone never wipes a previously selected discount.
   let isDisabled = shouldDisableDiscount(state, discount_key, {
      ignoreDealDeskLock: true,
   })
   // Full disable state (includes deal desk locks, permission-aware) — controls whether the
   // checkbox is greyed out / locked from editing.
   let isInputDisabled = shouldDisableDiscount(state, discount_key)

   // Check if this discount has DefaultOn property
   let hasDefaultOn = state.discount_list[discount_key]?.['DefaultOn'] ?? false

   // Use pure function to determine if checkbox should be checked
   const discountValue = state.promotionalDiscounts[discount_key]
   let isChecked = shouldDiscountBeChecked(
      discountValue,
      hasDefaultOn,
      shouldAlwaysApply,
      isDisabled,
   )

   // Single useEffect for handling all discount types with clear priority
   useEffect(() => {
      // Create a function to update state to avoid repetition
      const updateDiscountState = (value: boolean) => {
         if (state.promotionalDiscounts[discount_key] !== value) {
            state.setPromotionalDiscounts((prev) => ({
               ...prev,
               [discount_key]: value,
            }))
         }
      }

      // Priority 1: AlwaysApply discounts that aren't disabled
      if (shouldAlwaysApply && !isDisabled) {
         updateDiscountState(true)
         return
      }

      // Priority 2: Disabled discounts
      if (isDisabled) {
         updateDiscountState(false)
         return
      }

      // Priority 3: DefaultOn discounts that haven't been explicitly set
      if (hasDefaultOn && discountValue === undefined) {
         updateDiscountState(true)
         return
      }

      // Priority 4: Regular discounts based on isChecked
      if (isChecked && discountValue !== true) {
         updateDiscountState(true)
         return
      }
   }, [
      discount_key,
      discountValue,
      isDisabled,
      isChecked,
      hasDefaultOn,
      shouldAlwaysApply,
      state,
   ])

   return (
      <div className='row' style={{ order: order }}>
         <div className='col-bhr1'>
            <DiscountDescriptionBox
               discountDescription={
                  state.discount_list[discount_key]['DiscountDescription']
               }
               discountName={formattedDiscountName}
            />
         </div>
         <div className='col'>
            <input
               className='input-field'
               type='checkbox'
               checked={isChecked}
               disabled={shouldAlwaysApply || isInputDisabled}
               onChange={() => {
                  const newState = toggleDefaultOnDiscount(
                     state.promotionalDiscounts,
                     discount_key,
                     hasDefaultOn,
                  )
                  state.setPromotionalDiscounts(() => newState)
               }}
            ></input>
         </div>
         <div className='col'>
            {formatFloatAsPercent(
               state.discount_list[discount_key]['DiscountAmount'],
            )}
         </div>
      </div>
   )
}
